import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { type FormEvent, useState } from 'react'
import { AuthCard } from '@/components/auth-card'
import { Footer } from '@/components/footer'
import { NavBar } from '@/components/nav-bar'
import { TextInput } from '@/components/text-input'
import { PlatformApiError, signUp } from '@/lib/api'
import { friendlyErrorMessage } from '@/lib/errors'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/sign-up')({
  component: SignUpPage,
})

/**
 * Sign-up page with email-verification gate.
 *
 * Two states:
 *
 *   1. **Form**: user fills in name/email/password, submits. The
 *      backend creates the user row but returns `token: null`
 *      because `requireEmailVerification: true` on the platform
 *      auth config — no session is attached, so the user can't
 *      just navigate straight to /account.
 *
 *   2. **Sent**: show a "check your email" panel with the email
 *      address so the user knows where to look. The backend has
 *      fired the verification email via `sendVerificationEmail`,
 *      embedding a link that, when clicked, verifies the user
 *      AND signs them in (autoSignInAfterVerification: true) AND
 *      redirects them to /account.
 *
 * The user never touches /account from this page — the email
 * link does. If the user refreshes the page after submitting,
 * they just see the form again and can re-submit if they want a
 * fresh verification email.
 */
function SignUpPage() {
  const t = useTranslations('sign_up')

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      // Pass callbackURL so better-auth embeds the user's eventual
      // landing page in the verification link. After clicking the
      // link the backend sets the session cookie and 302s here,
      // so the user arrives already signed in.
      const callbackURL = `${window.location.origin}/account`
      await signUp({
        email,
        password,
        name: name || undefined,
        callbackURL,
      })
      // Backend returned `token: null` because verification is
      // required — transition to the "check your email" state.
      setSubmittedEmail(email)
    } catch (err) {
      // Client-side 4xx (validation, duplicate email, etc.)
      // carry useful messages we want to render verbatim.
      // Everything else goes through the classifier which maps
      // 5xx/network to "on our side" copy.
      if (err instanceof PlatformApiError && err.status >= 400 && err.status < 500) {
        setError(err.message || t('error_generic'))
      } else {
        setError(friendlyErrorMessage(err))
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (submittedEmail) {
    return (
      <>
        <NavBar />
        <main>
          <AuthCard
            title="Check your email"
            subtitle={`We sent a verification link to ${submittedEmail}.`}
            footer={
              <span>
                Wrong address or didn't get it?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setSubmittedEmail(null)
                    setError(null)
                  }}
                  className="font-medium text-primary hover:underline"
                >
                  start over
                </button>
                .
              </span>
            }
          >
            <p className="text-sm text-muted-foreground">
              Click the link in the email to verify your address and finish creating your account.
              The link expires in 60 minutes — if it runs out, just try signing in and we'll send
              you a fresh one automatically.
            </p>
            <div className="mt-6">
              <Link to="/sign-in" className="text-sm font-medium text-primary hover:underline">
                Back to sign in
              </Link>
            </div>
          </AuthCard>
        </main>
        <Footer />
      </>
    )
  }

  return (
    <>
      <NavBar />
      <main>
        <AuthCard
          title={t('title')}
          subtitle={t('subtitle')}
          footer={
            <span>
              {t('already_have_account')}{' '}
              <Link to="/sign-in" className="font-medium text-primary hover:underline">
                {t('sign_in_link')}
              </Link>
            </span>
          }
        >
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <TextInput
              label={t('name_label')}
              type="text"
              autoComplete="name"
              placeholder={t('name_placeholder')}
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
            />
            <TextInput
              label={t('email_label')}
              type="email"
              autoComplete="email"
              required
              placeholder={t('email_placeholder')}
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
            <TextInput
              label={t('password_label')}
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              hint={t('password_hint')}
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
            />

            {error ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className={cn(
                'w-full rounded-lg px-4 py-2.5 text-sm font-medium',
                'bg-primary text-primary-foreground shadow-md shadow-primary/20',
                'hover:opacity-90 transition-opacity',
                'disabled:opacity-60 disabled:cursor-not-allowed',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              )}
            >
              {submitting ? t('submitting') : t('submit')}
            </button>
          </form>
        </AuthCard>
      </main>
      <Footer />
    </>
  )
}
